import { Component, FunctionComponent, ReactNode, useEffect, useRef, useCallback } from "react";
import SoundService from "./services/SoundService";
import "./App.scss";
import "./game.scss";
import "./board.scss";
import "./menu.scss";
import "./ui.scss";
import MainMenu from "./MainMenu";
import AdminPanel from "./AdminPanel";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import NetworkedGameRoom, {
  loader as NetworkedGameRoomLoader,
} from "./NetworkedGameRoom";
import LocalGameRoom from "./LocalGameRoom";
import WipeTransition from "./WipeTransition";
import AuthScreen from "./AuthScreen";
import ToastContainer from "./ToastContainer";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { setUserProfile, setNewUser, updateProfileField } from "./store/userSlice";
import { getDB } from "./Firebase";
import { getUserProfile, updateLastSeen, USERS_COLLECTION } from "./services/FirebaseUsers";
import { onSnapshot, doc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const router = createBrowserRouter(
  [
    { path: "/",         element: <MainMenu /> },
    { path: "/local",    element: <LocalGameRoom /> },
    { path: "/admin",    element: <AdminPanel /> },
    { path: "/admin/*",  element: <AdminPanel /> },
    {
      path: "/:roomCode",
      element: <NetworkedGameRoom />,
      loader: NetworkedGameRoomLoader,
    },
  ],
  { basename: process.env.PUBLIC_URL || "/" }
);

class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: string | null }
> {
  state = { error: null };
  static getDerivedStateFromError(e: Error) { return { error: e.message }; }
  render() {
    if (this.state.error) {
      return (
        <div className="App-fatal-error">
          <div className="App-fatal-title">Произошла ошибка</div>
          <div className="App-fatal-msg">{this.state.error}</div>
          <button className="App-fatal-btn" onClick={() => window.location.reload()}>
            Перезагрузить
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const App: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const { isLoading, isNewUser } = useAppSelector((state) => state.user);
  const profileUnsubRef = useRef<(() => void) | null>(null);

    // Live-подписка на Firestore-профиль: обновляет coins/rating без перезагрузки
  const subscribeToProfile = useCallback((uid: string) => {
    if (profileUnsubRef.current) profileUnsubRef.current();
    const ref = doc(getDB(), USERS_COLLECTION, uid);
    profileUnsubRef.current = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      // Обновляем только те поля, которые явно есть в документе,
      // чтобы не затереть нулями поля которые ещё не записались в Firestore
      const update: Record<string, any> = {};
      if (data.coins            !== undefined) update.coins            = data.coins;
      if (data.rating           !== undefined) update.rating           = data.rating;
      if (data.level            !== undefined) update.level            = data.level;
      if (data.experience       !== undefined) update.experience       = data.experience;
      if (data.wins             !== undefined) update.wins             = data.wins;
      if (data.losses           !== undefined) update.losses           = data.losses;
      if (data.gamesPlayed      !== undefined) update.gamesPlayed      = data.gamesPlayed;
      if (data.winStreak        !== undefined) update.winStreak        = data.winStreak;
      if (data.longestWinStreak !== undefined) update.longestWinStreak = data.longestWinStreak;
      if (data.achievements     !== undefined) update.achievements     = data.achievements;
      if (Object.keys(update).length > 0) dispatch(updateProfileField(update));
    });
  }, [dispatch]);

  useEffect(() => {
    // onAuthStateChanged — основной и единственный источник истины об авторизации.
    // Срабатывает сразу при монтировании (с текущим user или null) и при любом изменении.
    const unsubAuth = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) {
        // Не авторизован → показываем экран входа/регистрации
        dispatch(setNewUser(""));
        return;
      }
      const profile = await getUserProfile(user.uid);
      if (profile) {
        dispatch(setUserProfile(profile));
        updateLastSeen(user.uid);
        subscribeToProfile(user.uid);
      } else {
        // Авторизован, но профиля ещё нет (сразу после регистрации, до записи в Firestore)
        dispatch(setNewUser(user.uid));
      }
    });

    return () => {
      profileUnsubRef.current?.();
      unsubAuth();
    };
  }, [dispatch, subscribeToProfile]);

  const unlockAudio = useCallback(() => {
    SoundService.instance.unlock();
  }, []);

  const profile = useAppSelector((state) => state.user.profile);

  // Забаненный пользователь — показываем заглушку
  if (!isLoading && !isNewUser && (profile as any)?.banned) {
    return (
      <div className="App-banned">
        <div className="App-banned-icon">🚫</div>
        <div className="App-banned-title">Аккаунт заблокирован</div>
        <div className="App-banned-sub">
          Ваш аккаунт заблокирован администратором.<br />
          Для обжалования: <a href="mailto:support@nardy.io">support@nardy.io</a>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ToastContainer />
        <div className="App-wrapper" onClick={unlockAudio} onTouchStart={unlockAudio}>
        <WipeTransition />

        {isLoading ? (
          <div className="App-init-loading">
            <div className="App-init-spinner" />
          </div>
        ) : isNewUser ? (
          <AuthScreen />
        ) : (
          <RouterProvider router={router} />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default App;
